//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import moment from 'moment-timezone';
import { PojoMap } from 'pojo-maps';
import { PojoSet } from 'pojo-sets';

import {
  InstagramPostDetailsValidationError,
  MAX_SHOP_TAG_COUNT,
} from '@instagram-account/redux/post-details/instagramPostDetailsModel';
import Cuid from '@models/Cuid';
import { IInstagramCarouselItem } from '@models/IInstagramCarouselItem';
import { getMediaMetadata, IMlsMediaItem } from '@models/IMlsMediaItem';
import { isGif } from '@models/INetworkItem';
import { IShopTag } from '@models/IShopTag';
import { ISchedulerDefaultPostingTime } from '@models/IUserSettings';
import MediaType from '@models/MediaType';
import UserId from '@models/UserId';
import { applyDefaultTimeToDate } from '@universal-scheduling/components/scheduler/modals/settings/schedulerSettingsModalUtils';
import { getMediaAspectRatio } from '@universal-scheduling/components/scheduler/post-details-tabs/postDetailsTabUtils';
import { isDefined } from '@utils/filterPredicates';
import { groupBy } from '@utils/groupBy';
import { parseHashtags } from '@utils/stringUtils';

export interface IMessageContentsOccurrenceLimitStatus {
  count: number;
  countAboveLimit: number;
  remainingBeforeLimit: number;
  isAboveLimit: boolean;
  isAboveWarningThreshold: boolean;
}

const MIN_IG_ASPECT_RATIO = 4 / 5;
const MAX_IG_ASPECT_RATIO = 1.91 / 1;

export const isValidIgImageAspectRatio = (aspectRatio: number): boolean => {
  return (
    aspectRatio >= MIN_IG_ASPECT_RATIO && aspectRatio <= MAX_IG_ASPECT_RATIO
  );
};

const MIN_IG_VIDEO_ASPECT_RATIO = 9 / 16;
const MAX_IG_VIDEO_ASPECT_RATIO = 1.91 / 1;

export const isValidIgVideoAspectRatio = (aspectRatio: number): boolean => {
  return (
    aspectRatio >= MIN_IG_VIDEO_ASPECT_RATIO &&
    aspectRatio <= MAX_IG_VIDEO_ASPECT_RATIO
  );
};

export const INSTAGRAM_FEED_MAX_VIDEO_DURATION_MILLIS = 60000;
export const INSTAGRAM_FEED_MIN_VIDEO_DURATION_MILLIS = 3000;

export const isValidIgVideoDuration = (
  videoDurationMillis: number,
): boolean => {
  return (
    videoDurationMillis >= INSTAGRAM_FEED_MIN_VIDEO_DURATION_MILLIS &&
    videoDurationMillis <= INSTAGRAM_FEED_MAX_VIDEO_DURATION_MILLIS
  );
};

export const INSTAGRAM_FEED_MAX_VIDEO_SIZE_BYTES = 100 * 1024 * 1024;

export const isValidIgVideoSize = (videoSizeBytes: number): boolean => {
  return videoSizeBytes <= INSTAGRAM_FEED_MAX_VIDEO_SIZE_BYTES;
};

export const INSTAGRAM_FEED_MAX_IMAGE_SIZE_BYTES = 8 * 1024 * 1024;

export const isValidIgImageSize = (imageSizeBytes: number): boolean => {
  return imageSizeBytes <= INSTAGRAM_FEED_MAX_IMAGE_SIZE_BYTES;
};

export const MAX_IG_HASHTAG_QUANTITY = 30;

/**
 * An Instagram message (caption, comment, description, etc...) is
 * within the warning threshold if there are fewer than this number
 * of hashtags remaining before the maximum limit
 */
export const IG_HASHTAG_QUANTITY_REMAINING_WARNING_THRESHOLD = 10;

export const MAX_IG_MESSAGE_LENGTH = 2200;

/**
 * An Instagram message (caption, comment, description, etc...) is
 * within the warning threshold if there are fewer than this number
 * of characters remaining before the maximum limit
 */
export const IG_MESSAGE_LENGTH_REMAINING_WARNING_THRESHOLD = 200;

/**
 * Given an Instagram message (caption, comment, etc...) returns validation
 * status regarding Hashtags within the message
 *
 * @param message The message to be analysed for hashtag limits
 */
export const getMessageHashtagOccurrenceStatus = (
  message?: string,
): IMessageContentsOccurrenceLimitStatus => {
  if (!isDefined(message)) {
    return {
      count: 0,
      countAboveLimit: 0,
      remainingBeforeLimit: 0,
      isAboveLimit: false,
      isAboveWarningThreshold: false,
    };
  }

  const count = parseHashtags(message).length;

  const remainingBeforeLimit = MAX_IG_HASHTAG_QUANTITY - count;

  const isAboveLimit = remainingBeforeLimit < 0;
  const countAboveLimit = isAboveLimit ? Math.abs(remainingBeforeLimit) : 0;
  const isAboveWarningThreshold =
    remainingBeforeLimit <= IG_HASHTAG_QUANTITY_REMAINING_WARNING_THRESHOLD;

  return {
    count,
    countAboveLimit,
    remainingBeforeLimit,
    isAboveLimit,
    isAboveWarningThreshold,
  };
};

/**
 * Given an Instagram message (caption, comment, etc...) returns validation
 * status regarding characters within the message
 *
 * @param message The message to be analysed for character limits
 */
export const getMessageCharacterOccurrenceStatus = (
  message?: string,
): IMessageContentsOccurrenceLimitStatus => {
  if (!isDefined(message)) {
    return {
      count: 0,
      countAboveLimit: 0,
      remainingBeforeLimit: 0,
      isAboveLimit: false,
      isAboveWarningThreshold: false,
    };
  }

  const count = message.length;

  const remainingBeforeLimit = MAX_IG_MESSAGE_LENGTH - count;

  const isAboveLimit = remainingBeforeLimit < 0;
  const countAboveLimit = isAboveLimit ? Math.abs(remainingBeforeLimit) : 0;
  const isAboveWarningThreshold =
    remainingBeforeLimit <= IG_MESSAGE_LENGTH_REMAINING_WARNING_THRESHOLD;

  return {
    count,
    countAboveLimit,
    remainingBeforeLimit,
    isAboveLimit,
    isAboveWarningThreshold,
  };
};

/**
 * Check for user-fixable errors on feed post specific form values that
 * will block a user from succesfully scheduling the current post.
 */
export const validateFeedPostDetails = (
  selectedCuids: Cuid[],
  scheduleMediaMap: PojoMap<Cuid, IMlsMediaItem>,
  caption?: string,
): {
  formErrors: PojoSet<InstagramPostDetailsValidationError>;
  invalidCuids: PojoSet<Cuid>;
} => {
  let formErrors = PojoSet.empty<InstagramPostDetailsValidationError>();
  let invalidCuids = PojoSet.empty<Cuid>();

  // Caption must be valid length and not have too many hashtags
  if (isDefined(caption)) {
    if (getMessageHashtagOccurrenceStatus(caption).isAboveLimit) {
      formErrors = PojoSet.add(
        formErrors,
        InstagramPostDetailsValidationError.TooManyHashtags,
      );
    } else if (getMessageCharacterOccurrenceStatus(caption).isAboveLimit) {
      formErrors = PojoSet.add(
        formErrors,
        InstagramPostDetailsValidationError.CaptionTooLong,
      );
    }
  }

  // Posts and stories must have at least one piece of valid media
  if (selectedCuids.length < 1) {
    formErrors = PojoSet.add(
      formErrors,
      InstagramPostDetailsValidationError.NoMedia,
    );
  } else {
    selectedCuids.forEach((cuid) => {
      const mediaItem = PojoMap.get(scheduleMediaMap, cuid);
      const networkItem = mediaItem?.networkItem;

      if (isDefined(mediaItem) && isDefined(networkItem)) {
        if (isGif(networkItem)) {
          formErrors = PojoSet.add(
            formErrors,
            InstagramPostDetailsValidationError.Gif,
          );
          invalidCuids = PojoSet.add(invalidCuids, cuid);
        }

        const mediaMetadata = getMediaMetadata(mediaItem);
        const aspectRatio = getMediaAspectRatio(
          mediaMetadata?.width,
          mediaMetadata?.height,
        );

        const isValidAspectRatio =
          networkItem.mediaType === MediaType.Video
            ? isValidIgVideoAspectRatio(aspectRatio)
            : isValidIgImageAspectRatio(aspectRatio);

        if (!isValidAspectRatio) {
          formErrors = PojoSet.add(
            formErrors,
            InstagramPostDetailsValidationError.InvalidMediaAspectRatio,
          );
          invalidCuids = PojoSet.add(invalidCuids, cuid);
        }

        const videoDuration = networkItem.mediaMetadata?.durationMillis;
        if (
          isDefined(videoDuration) &&
          !isValidIgVideoDuration(videoDuration)
        ) {
          formErrors = PojoSet.add(
            formErrors,
            InstagramPostDetailsValidationError.InvalidVideoLength,
          );
          invalidCuids = PojoSet.add(invalidCuids, cuid);
        }

        const fileSize = networkItem.mediaMetadata?.fileSize;
        if (isDefined(fileSize)) {
          if (
            networkItem.mediaType === MediaType.Video &&
            !isValidIgVideoSize(fileSize)
          ) {
            formErrors = PojoSet.add(
              formErrors,
              InstagramPostDetailsValidationError.InvalidFeedVideoSize,
            );
            invalidCuids = PojoSet.add(invalidCuids, cuid);
          } else if (
            networkItem.mediaType === MediaType.Image &&
            !isValidIgImageSize(fileSize)
          ) {
            formErrors = PojoSet.add(
              formErrors,
              InstagramPostDetailsValidationError.InvalidIgImageSize,
            );
            invalidCuids = PojoSet.add(invalidCuids, cuid);
          }
        }
      }
    });
  }

  return {
    formErrors,
    invalidCuids,
  };
};

/**
 * Check for user-fixable errors on non post-type specific form values that
 * will block a user from succesfully scheduling the current post.
 */
export const validateSharedPostDetailsFormInputs = (
  notificationUserId?: UserId,
  postTime?: Date,
  firstComment?: string,
  shopTags?: IShopTag[],
): PojoSet<InstagramPostDetailsValidationError> => {
  let formErrors = PojoSet.empty<InstagramPostDetailsValidationError>();

  // Posts and stories must have a notification user selected
  if (!isDefined(notificationUserId)) {
    formErrors = PojoSet.add(
      formErrors,
      InstagramPostDetailsValidationError.NoNotificationUser,
    );
  }

  // Posts and stories must have a post time selected
  if (!isDefined(postTime)) {
    formErrors = PojoSet.add(
      formErrors,
      InstagramPostDetailsValidationError.NoPostTime,
    );
    // The selected post time must be in the future
  } else if (postTime < new Date()) {
    formErrors = PojoSet.add(
      formErrors,
      InstagramPostDetailsValidationError.PostTimeInPast,
    );
  }

  // First comment must be valid length and not have too many hashtags
  if (isDefined(firstComment)) {
    if (getMessageHashtagOccurrenceStatus(firstComment).isAboveLimit) {
      formErrors = PojoSet.add(
        formErrors,
        InstagramPostDetailsValidationError.FirstCommentTooManyHashtags,
      );
    } else if (getMessageCharacterOccurrenceStatus(firstComment).isAboveLimit) {
      formErrors = PojoSet.add(
        formErrors,
        InstagramPostDetailsValidationError.FirstCommentTooLong,
      );
    }
  }

  const shopTagsByMedia = groupBy(
    shopTags ?? [],
    (shopTag) => shopTag.mediaIndex,
  );

  if (
    Object.values(shopTagsByMedia).some(
      (tags) => tags.length > MAX_SHOP_TAG_COUNT,
    )
  ) {
    formErrors = PojoSet.add(
      formErrors,
      InstagramPostDetailsValidationError.TooManyShopTags,
    );
  }

  return formErrors;
};

/**
 * Applies the default post time to the current day if the time has not passed yet,
 * or applies it to the following day if it has. Returns no date if there is no default.
 */
export const getInitialPostTime = (
  defaultPostTime?: ISchedulerDefaultPostingTime,
) => {
  if (
    defaultPostTime &&
    isDefined(defaultPostTime.hour) &&
    isDefined(defaultPostTime.minute)
  ) {
    const todayWithDefault = applyDefaultTimeToDate(
      new Date(),
      defaultPostTime,
    );
    return todayWithDefault < new Date()
      ? applyDefaultTimeToDate(moment().add(1, 'd').toDate(), defaultPostTime)
      : todayWithDefault;
  }
};

/**
 * Given a scheduled or draft post's base media item and carousel media,
 * return the list of selected cuids to show in the editing state for this post.
 */
export const getSelectedCuidsForPost = (
  baseMediaItem?: IMlsMediaItem,
  carouselMedia: IInstagramCarouselItem[] = [],
): Cuid[] => {
  const baseCuid = baseMediaItem?.cuid;

  const baseCuidList = baseCuid ? [baseCuid] : [];
  const carouselCuids = carouselMedia
    .filter((carouselPost) => carouselPost.mediaItem)
    .map((carouselPost) => carouselPost.mediaItem.cuid);

  return [...baseCuidList, ...carouselCuids];
};

/**
 * We create a default caption from the original caption with a
 * username attribution to help clients easily repost UGC.
 */
export const createDefaultCaption = (mediaItem?: IMlsMediaItem) => {
  const caption = mediaItem?.networkItem?.caption;
  const postingUsername = mediaItem?.networkUser?.username;

  if (caption) {
    return postingUsername ? `${caption} (via @${postingUsername})` : caption;
  }
};
